import { forwardRef, useState, type TextareaHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { borderRadius } from '../../tokens/spacing';
import { transitions } from '../../utils/motion';

type TextareaResize = 'both' | 'vertical' | 'horizontal' | 'none';
type TextareaSize = 'sm' | 'md' | 'lg';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  textareaSize?: TextareaSize;
  resize?: TextareaResize;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      textareaSize = 'md',
      resize = 'vertical',
      id,
      disabled,
      readOnly,
      className,
      rows = 3,
      ...props
    },
    ref,
  ): JSX.Element => {
    const textareaId = id ?? (label ? `textarea-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);
    const errorId = textareaId ? `${textareaId}-error` : undefined;
    const helperId = textareaId ? `${textareaId}-helper` : undefined;
    const [focused, setFocused] = useState(false);

    const sizeStyles: Record<TextareaSize, { padding: string; fontSize: string }> = {
      sm: { padding: '0.375rem 0.625rem', fontSize: '0.8125rem' },
      md: { padding: '0.5rem 0.75rem', fontSize: '0.875rem' },
      lg: { padding: '0.625rem 0.875rem', fontSize: '1rem' },
    };

    const currentSize = sizeStyles[textareaSize];

    const resizeMap: Record<TextareaResize, string> = {
      both: 'both',
      vertical: 'vertical',
      horizontal: 'horizontal',
      none: 'none',
    };

    return (
      <div
        className={cn('ds-textarea-wrapper', className)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem',
          width: '100%',
          /* `flex` e `minWidth` para o campo se comportar nos DOIS contextos:
             em pai block, `width: 100%` manda e o campo preenche a linha; em
             pai flex (toolbar de filtro), `flex-basis: 0` deixa os campos
             dividirem a linha em vez de cada um reivindicar 100% e quebrar
             para a propria linha.

             Sem isso, a toolbar de /fiscal/invoices — que ja era `display:
             flex` — renderizava os tres filtros EMPILHADOS, cada um com a
             largura inteira do card. Visto na captura. */
          flex: '1 1 0%',
          minWidth: 0,
        }}
      >
        {label && (
          <label
            htmlFor={textareaId}
            className="ds-textarea__label"
            style={{
              /* label-md do prototipo: 12px JetBrains Mono, 0.02em, 500,
                 em on-surface-variant (medido em 168 labels de campo) */
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: '12px',
              lineHeight: '16px',
              letterSpacing: '0.02em',
              fontWeight: 500,
              color: disabled ? '#74777f' : '#43474e',
            }}
          >
            {label}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          disabled={disabled}
          readOnly={readOnly}
          rows={rows}
          aria-invalid={!!error}
          aria-describedby={
            [error ? errorId : null, !error && helperText ? helperId : null].filter(Boolean).join(' ') || undefined
          }
          className={cn('ds-textarea__field', error && 'ds-textarea__field--error', focused && 'ds-textarea__field--focused')}
          style={{
            width: '100%',
            padding: currentSize.padding,
            fontSize: currentSize.fontSize,
            lineHeight: 1.5,
            color: disabled ? '#74777f' : '#191c1e',
            background: disabled ? '#f2f4f6' : readOnly ? '#f2f4f6' : '#f7f9fb',
            border: `1px solid ${error ? '#ba1a1a' : focused ? '#002045' : disabled ? '#eceef0' : '#c4c6cf'}`,
            borderRadius: borderRadius.sm,
            transition: transitions.fast,
            outline: 'none',
            fontFamily: 'inherit',
            cursor: disabled ? 'not-allowed' : readOnly ? 'default' : 'text',
            boxSizing: 'border-box',
            resize: resizeMap[resize] as TextareaResize,
            boxShadow: focused
              ? error
                ? '0 0 0 1px #ba1a1a'
                : '0 0 0 1px #002045'
              : 'none',
          }}
          onFocus={(e) => {
            setFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            props.onBlur?.(e);
          }}
          {...props}
        />

        {error && (
          <p
            id={errorId}
            className="ds-textarea__error"
            role="alert"
            style={{ margin: 0, fontSize: '0.75rem', color: '#ba1a1a' }}
          >
            {error}
          </p>
        )}

        {helperText && !error && (
          <p
            id={helperId}
            className="ds-textarea__helper"
            style={{ margin: 0, fontSize: '0.75rem', color: '#43474e' }}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
